import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, Send, Star, X } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";

function ReviewCard({ r }) {
  return (
    <article className="snap-center w-[78vw] max-w-[400px] shrink-0 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur px-4 py-3.5">
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-1.5 min-w-0">
          <Star size={13} className="text-[#FFD400] shrink-0" fill="#FFD400" />
          <span className="font-bold text-sm truncate">{r.name}</span>
        </span>
        <span className="text-[11px] text-[#647089] font-mono whitespace-nowrap">{r.date}</span>
      </div>
      <p className="review-text mt-1.5 text-xs text-[#C6D3E6] leading-relaxed">{r.text}</p>
    </article>
  );
}

function LeaveModal({ open, onClose }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", text: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setForm({ name: "", text: "" });
      setSent(false); setErr(""); setBusy(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const submit = async () => {
    if (busy) return;
    const name = form.name.trim();
    const text = form.text.trim();
    if (name.length < 2) { setErr(t.review_err_name); return; }
    if (text.length < 10) { setErr(t.review_err_text); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/public/reviews", {
        name, text, lang: lang === "en" ? "en" : "uk",
      });
      setSent(true);
    } catch {
      setErr(t.review_err_send);
    } finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[90] bg-[#060A14]/80 backdrop-blur-sm flex items-center justify-center px-5"
      onClick={onClose} data-testid="review-modal" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0E1526] p-6"
        style={{ boxShadow: "0 24px 80px rgba(0,0,0,0.55)" }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-xl font-extrabold uppercase">{t.review_modal_title}</h3>
            {!sent && (
              <p className="text-xs text-[#9FB0C7] mt-1.5 leading-relaxed">{t.review_modal_sub}</p>
            )}
          </div>
          <button onClick={onClose} aria-label={t.review_close}
            className="grid place-items-center w-8 h-8 rounded-full border border-white/10 text-[#9FB0C7] hover:text-white hover:border-white/30 transition-colors shrink-0">
            <X size={16} />
          </button>
        </div>

        {sent ? (
          <div className="py-6 text-center">
            <div className="mx-auto grid place-items-center w-14 h-14 rounded-full grad-tg">
              <Star size={24} className="text-white" fill="#fff" />
            </div>
            <p className="mt-4 font-bold">{t.review_sent_title}</p>
            <p className="mt-1.5 text-sm text-[#9FB0C7] leading-relaxed">{t.review_sent_text}</p>
            <button onClick={onClose}
              className="mt-5 btn-tg grad-tg text-white font-bold px-6 py-3 rounded-2xl">
              {t.review_close}
            </button>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <input value={form.name} maxLength={40}
              onChange={(e) => { setForm((f) => ({ ...f, name: e.target.value })); setErr(""); }}
              placeholder={t.review_name_ph}
              className="w-full rounded-2xl bg-[#0B1220] border border-white/10 focus:border-[#2BA8FF] outline-none px-4 py-3 text-base" />
            <textarea value={form.text} rows={4} maxLength={1000}
              onChange={(e) => { setForm((f) => ({ ...f, text: e.target.value })); setErr(""); }}
              placeholder={t.review_text_ph}
              className="w-full rounded-2xl bg-[#0B1220] border border-white/10 focus:border-[#2BA8FF] outline-none p-4 text-base leading-relaxed resize-none" />
            {err && <p className="text-sm text-[#FF8A8A]">{err}</p>}
            <button data-testid="review-submit" onClick={submit} disabled={busy}
              className="btn-tg grad-tg text-white font-bold w-full py-3.5 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-50">
              <Send size={16} /> {t.review_send}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default function ReviewsRibbon() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/public/reviews", { params: { lang: lang === "en" ? "en" : "uk" } })
      .then((r) => setItems(r.data?.items || []))
      .catch(() => {});
  }, [lang]);

  return (
    <section className="mt-4 sm:mt-0">
      <h3 className="font-display text-lg sm:text-xl font-extrabold uppercase">{t.reviews_title}</h3>

      {items.length === 0 ? (
        <p className="mt-3 text-sm text-[#647089]">{t.reviews_empty}</p>
      ) : (
        <div className="reviews-fade mt-4 overflow-x-auto no-scrollbar snap-x snap-mandatory flex gap-3.5 pb-1"
          style={{
            paddingLeft: "calc((100% - min(78vw, 400px)) / 2)",
            paddingRight: "calc((100% - min(78vw, 400px)) / 2)",
            scrollPaddingLeft: "calc((100% - min(78vw, 400px)) / 2)",
            scrollPaddingRight: "calc((100% - min(78vw, 400px)) / 2)",
          }}>
          {items.map((r, i) => <ReviewCard key={i} r={r} />)}
        </div>
      )}

      <div className="mt-3 flex justify-end">
        <button data-testid="reviews-leave" onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 text-sm text-[#8CCBFF] hover:text-white transition-colors">
          {t.reviews_leave} <ArrowRight size={14} />
        </button>
      </div>

      <LeaveModal open={open} onClose={() => setOpen(false)} />
    </section>
  );
}
